import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  AuctionItemSell,
  AuctionService,
  AuthService,
  BatchPostingService,
  BuyerDealerResult,
  LaunchDarklyService,
  ReservationRequestDto,
  ReservationResponseDto,
  ReservationsService
} from 'remarketing-angular-library';
import {Store} from '@ngxs/store';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {finalize, take, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Subject} from 'rxjs';
import {get} from 'lodash';
import {ChangeStatusModalDirective} from '../change-status-modal.directive';
import {
  DropdownSelectComponent,
  ErrorIconComponent,
  SpinnerComponent,
  TrapFocusComponent
} from 'remarketing-ui-components-lib';
import { SearchBoxInputComponent } from '../../search-box-input/search-box-input.component';
import { CommonModule } from '@angular/common';
import { AnalyticsService } from 'smartauction-frontend-micro-library';

@Component({
    selector: 'app-make-reserve-modal',
    templateUrl: './make-reserve-modal.component.html',
    styleUrls: ['./make-reserve-modal.component.scss'],
    imports: [
        SpinnerComponent,
        ErrorIconComponent,
        TranslocoDirective,
        TrapFocusComponent,
        DropdownSelectComponent,
        SearchBoxInputComponent,
        CommonModule
    ]
})
export class MakeReserveModalComponent extends ChangeStatusModalDirective implements OnInit {
  @Input() numberOfVehicles = 1;
  @Input() isReserved = false;
  @Output() close = new EventEmitter();
  _destroyed$ = new Subject();
  isSelectTextError$ = new BehaviorSubject<boolean>(false);
  isSelectTextErrorValue = this.isSelectTextError$.asObservable();

  isError: boolean;
  isLoading: boolean;
  isSelectOptionSelected: boolean;
  isIsvFileProcessing = true;
  dropDownFocusTrap = false;
  selectedDealership;
  textHeader: string;
  textSubHeader: string;
  textDealership: string;
  textSelect: string;
  textError: string;
  selectTextError: string;

  private readonly analyticsService: AnalyticsService;

  get isDealerSeller(): boolean {
    return this.authService.getRoles().includes('Dealer_Seller');
  }

  get isIsvReservationHaltEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-reservationHaltEnabled', false);
  }

  get isIsvFileUploadHaltEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-fileProcessingReservationHaltEnabled', false);
  }

  constructor(
    store: Store,
    translocoService: TranslocoService,
    auctionService: AuctionService,
    private readonly reservationService: ReservationsService,
    private readonly authService: AuthService,
    private readonly batchPostingService: BatchPostingService,
    readonly launchDarklyService: LaunchDarklyService
  ) {
    super(store, auctionService, launchDarklyService, translocoService);
    this.analyticsService = inject(AnalyticsService);
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.getTranslations();
    if (this.isIsvReservationHaltEnabled) {
      this.isIsvFileProcessing = true;
    } else if (this.isIsvFileUploadHaltEnabled) {
      this.batchPostingService.checkReservationsEnabled().pipe(take(1)).subscribe({
        next: value => {
          this.isIsvFileProcessing = !value.reservationEnabled;
        },
        error: error => {
          console.log(error);
        }
      });
    } else {
      this.isIsvFileProcessing = false;
    }
  }

  makeReserve() {
    this.isLoading = true;
    if (this.isIsvReservationHaltEnabled) {
      this.setReservationErrors();
    } else if (this.isIsvFileUploadHaltEnabled) {
      this.batchPostingService.checkReservationsEnabled().pipe(take(1)).subscribe({
        next: value => {
          if (value.reservationEnabled) {
            this.performReservation();
          } else {
            this.setReservationErrors();
          }
        }
      }
      );
    } else {
      this.performReservation();
    }
  }

  private performReservation() {
    const auctionItemUuids = [];
    this.selectedVehicles.forEach(vehicle => {
      // only adding ISV vehicles to the request to reserve
      if (this.getVehicleTypeCode(vehicle) === 'ISV' && (vehicle.auctionItemStatus === 'ACTIVE' || this.isChangeReservedDealershipEnabled)) {
        auctionItemUuids.push(vehicle.auctionItemUuid);
      }
    });

    const payload = {
      auctionItemUuids: auctionItemUuids,
      buyerOeUuid: this.selectedDealership,
      reservationStatus: 'RESERVE'
    } as ReservationRequestDto;

    if (this.selectedDealership !== undefined && !this.isSelectOptionSelected) {
      (this.isReserved ? this.reservationService.updateVehicleReservations(payload) : this.reservationService.postVehicleReservations(payload))
        .pipe(take(1))
        .subscribe({
          next: (response: ReservationResponseDto[]) => {
            if (response) {
              this.changeReserveStatus('RESERVED', response);
              this.isLoading = false;
              this.analyticsTrackResponseSuccessErrors(response);
            }
          },
          error: error => {
            if (error) {
              this.isLoading = false;
              this.isError = true;
            }
          }
        });
    } else {
      this.isLoading = false;
      this.isSelectTextError$.next(true);
    }
  }

  setReservationErrors() {
    this.isLoading = false;
    this.isError = true;
    this.textError = 'Reservation changes are disabled while inventory is being uploaded.';
  }

  closeModal() {
    this.isLoading = false;
    this.isError = false;
    this.close.emit();
  }

  getVehicleTypeCode(vehicle: AuctionItemSell) {
    return get(vehicle, 'vehicleTypeCode', null);
  }

  getTranslations() {
    super.getTranslations();
    const keyParams = {
      'sellModule.manageInventory.actionListModal.makeReserveModal.textHeader': {
        numberOfVehicles: this.numberOfVehicles
      },
      'sellModule.manageInventory.actionListModal.makeReserveModal.textSubHeader': {},
      'sellModule.manageInventory.actionListModal.makeReserveModal.textDealership': {},
      'sellModule.manageInventory.actionListModal.makeReserveModal.textSelect': {},
      'sellModule.manageInventory.actionListModal.makeReserveModal.selectTextError': {},
      'app.layout': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(translate => {
        this.textHeader = translate[0];
        this.textSubHeader = translate[1];
        this.textDealership = translate[2];
        this.textSelect = translate[3];
        this.selectTextError = translate[4];
        this.textError = translate[5].cantCompleteRequestError;
      });
  }

  analyticsTrackResponseSuccessErrors(response: ReservationResponseDto[]) {
    const uniqueErrorMessages = new Map<string, string>();
    response.forEach(item => {
      item.errors?.forEach(error => {
        if (!uniqueErrorMessages.has(error.errorCode)) {
          uniqueErrorMessages.set(error.errorCode, error.errorMessage);
        }
      });
    });
    uniqueErrorMessages.forEach((code, message) => {
      this.analyticsTrackError(code, message);
    });
  }

  analyticsTrackError(errorCode: string, errorMessage: string) {
    this.analyticsService.track('customError', errorMessage, errorCode);
  }

  onDealerSelected(buyerDealerResult: BuyerDealerResult) {
    this.selectedDealership = buyerDealerResult.oeUuid;
  }
}
